import { Injectable, Injector, inject } from '@angular/core';
import { LayoutStoreService, DB_CONNECTION_CONFIGS, PROJECT_PROVIDER } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';

/**
 * bpweb-Konfiguration des Druck-Layout-Stores (dedizierte DB `bpweb-layouts`,
 * ein Doc je Layout). Lifecycle/CRUD/Sync kommen aus {@link LayoutStoreService};
 * hier nur Remote-URL (inkl. Credentials) und Name der lokalen Replik — analog
 * zur ZeichnungenService.
 */
@Injectable({ providedIn: 'root' })
export class LayoutsService extends LayoutStoreService {
	private dbConfig = inject(DatabaseConfigService);
	private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);
	private injector = inject(Injector);

	/**
	 * Gruppen des Projekts (feature/user-groups): neue/geaenderte Layout-Docs erben additiv die
	 * groups des Projekts. Lazy ueber PROJECT_PROVIDER (Injektions-Kreis vermeiden). Nur wenn das
	 * aktuelle Projekt-Doc zur projectId passt (sonst keine Stempelung).
	 */
	protected override groupsForProject(projectId: string): string[] | undefined {
		const ps = this.injector.get(PROJECT_PROVIDER, null) as { currentProjectDoc?: () => { _id?: string; groups?: string[] } | null } | null;
		const doc = ps?.currentProjectDoc?.();
		return doc && doc._id === projectId ? doc.groups : undefined;
	}

	/** Remote-Master = `bpweb-layouts` (nur online). */
	protected get remoteDbUrl(): string | null {
		if (!navigator.onLine) return null;
		const conn = this.dbConnectionConfigs[0];
		return conn ? conn.url(this.dbConfig.layoutsDbName) : null;
	}

	/** Lokale Replik (konstanter Name). */
	protected get localCacheDbName(): string {
		return `${this.dbConfig.layoutsDbName}-cache`;
	}

	/** Gateway-Auth: Bearer-Header pro Request für die Remote-Replikation. */
	protected override authHeaders(): Record<string, string> | null | undefined {
		return this.dbConnectionConfigs[0]?.getAuthHeaders?.() ?? null;
	}

	/**
	 * Abgelaufener Token: einmal still erneuern, dann wiederholt der fetch-Wrapper
	 * die Anfrage. Ohne das blieb die Replikation nach Token-Ablauf bei 401 stehen,
	 * bis die Seite neu geladen wurde.
	 */
	protected override authRefresh(): Promise<boolean> {
		return this.dbConnectionConfigs[0]?.refreshAuth?.() ?? Promise.resolve(false);
	}

	/**
	 * Auf dem Nur-Lese-Weg festgefahren: zurueck auf die richtige Adresse.
	 *
	 * Die Verbindung waehlt ihren Weg nur beim Aufbau. Startet die App ohne
	 * greifbaren Token (Standby, wackliges Netz), bleibt sie auf
	 * `/db/public/...` stehen — Ansehen geht, Speichern bekommt ein 405, und
	 * das fuer die ganze Sitzung (Nutzer-Befund 01.09.2026).
	 */
	protected override adressReparatur(url: string): string | undefined {
		return this.dbConnectionConfigs[0]?.repariereAdresse?.(url);
	}
}
